import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { view, dialogs } from '@yola/ws-sdk';
import { Spring } from 'react-spring/renderprops';
import helpCenter from 'yola-editor/src/js/modules/help-center';
import getSameBoundsElements from '../../common/helpers/get-same-bounds-elements';
import findAllFor from '../filters/find-all-for';
import operations from '../operations';
import ToolRender from '../components/tool-render';
import { SELECT, FOCUS, HOVER } from '../constants/trigger-types';

const animationConfig = {
  from: {
    scale: 0.6,
    opacity: 0,
  },
  to: {
    scale: 1,
    opacity: 1,
  },
  config: { tension: 250, friction: 16 },
};

function collectCustomTools(elements, triggerType, ownerElementId = null) {
  const seen = new Set();

  return elements.reduce((tools, element) => {
    const matchedTools = findAllFor(element, triggerType);

    if (!matchedTools.length) {
      return tools;
    }

    const uniqueTools = [];

    matchedTools.forEach((tool) => {
      const { id, getTargetElement, container, triggerOn } = tool;
      const targetElement = getTargetElement ? getTargetElement(element) : element;
      const elementId = view.accessors.getLiveElementId(targetElement);

      // On select, a tool that redirects its target to another (ancestor) element via
      // `getTargetElement` — e.g. column-resize pointing to the parent column — must only
      // activate when that owner element is the one actually selected. This prevents
      // selecting a child element (e.g. an image widget) from activating an ancestor's
      // tool, while tools without redirection (e.g. image-resize) keep working as before.
      if (ownerElementId && getTargetElement && elementId !== ownerElementId) {
        return;
      }

      if (!seen.has(id)) {
        seen.add(id);
        uniqueTools.push({
          id,
          elementId,
          container,
          triggerOn,
        });
      }
    });

    return tools.concat(uniqueTools);
  }, []);
}

function CustomToolsContainer() {
  const {
    selectedElementId,
    scrollPosition,
    isLoaded,
    focusedElementId,
    hoveredElementId,
    isDialogVisible,
  } = useSelector(
    (state) => ({
      selectedElementId: view.selectors.getSelectedElement(state),
      scrollPosition: view.selectors.getScrollPosition(state),
      isLoaded: view.selectors.getLoadedStatus(state),
      focusedElementId: view.selectors.getFocusedElement(state),
      hoveredElementId: view.selectors.getHoveredElement(state),
      isDialogVisible: dialogs.verifiers.isVisible(),
    }),
    shallowEqual
  );

  const [activeTool, setActiveTool] = useState(null);

  const savedSelectedElementId = useRef(null);
  const handleActionEnd = useCallback(() => {
    savedSelectedElementId.current = null;
    setActiveTool(null);
    operations.setActiveTool(null);
  }, []);

  const handleActionStart = useCallback((tool, liveElementId) => {
    savedSelectedElementId.current = liveElementId;
    setActiveTool(tool);
    const triggers = tool.triggerOn;
    const isHoverTool = Array.isArray(triggers) ? triggers.includes(HOVER) : triggers === HOVER;
    if (!isHoverTool) {
      view.operations.setHoveredElement(null);
    }
    operations.setActiveTool(tool.id);
  }, []);

  // Since the Help Center widget can be rendered inside an iframe, it may interfere
  // with certain tools—such as the resize tool. To avoid this, we need to hide the widget
  // when the user is using specific tools.
  useEffect(() => {
    if (activeTool) {
      helpCenter.operations.hideForm();
    }

    return () => {
      if (activeTool) {
        helpCenter.operations.showForm();
      }
    };
  }, [activeTool]);

  // We do not need to show any custom tool when there opened dialog
  if (isDialogVisible) return null;

  // Why isLoaded is so important?(For better understanding you have to know how SyncyFrame component works)
  //
  // When editor reloads\changes page, we have some time(amount depends on your internet connection)
  // to iterate with elements on the page, which will be changed soon. And in that cases, if we
  // call any matches function from extensions, we will face the trouble that our element's document,
  // in some moment of time, will lose window context and editor will throw errors.
  // To prevent this behavior we added checking isLoaded status.
  if (!isLoaded) return null;

  let customTools = [];
  let liveElementId;

  if (focusedElementId) {
    const focusedElement = view.accessors.getLiveElement(focusedElementId);
    const elements = focusedElement ? [focusedElement] : [];

    customTools = collectCustomTools(elements, FOCUS);
    liveElementId = focusedElementId;
  } else if (selectedElementId || hoveredElementId) {
    const element = view.accessors.getLiveElement(selectedElementId || hoveredElementId);
    const sameBoundsElements = element ? getSameBoundsElements(element) : [];

    customTools = collectCustomTools(
      sameBoundsElements,
      selectedElementId ? SELECT : HOVER,
      // On select, restrict redirecting tools to the selected element so that selecting a
      // child element does not activate an ancestor's tool. On hover we pass no owner, so
      // ancestor tools are kept (e.g. to show the column grid while hovering a child).
      selectedElementId || null
    );
    liveElementId = selectedElementId || hoveredElementId;
  }

  if (!liveElementId || !customTools.length) {
    return null;
  }

  const toolsList = activeTool ? [activeTool] : customTools;

  return (
    <div className="ws-custom-tools">
      {activeTool && (
        <div
          className="ws-custom-tools__overlay"
          style={{ transform: `translate3d(0, ${scrollPosition}px, 0)` }}
        />
      )}

      {toolsList.map((tool) => {
        const { id, elementId } = tool;
        const key = activeTool
          ? `${savedSelectedElementId.current}_${elementId}_${id}`
          : `${liveElementId}_${elementId}_${id}`;

        return (
          <Spring
            key={key}
            from={animationConfig.from}
            to={animationConfig.to}
            config={animationConfig.config}
          >
            {(interpolatingStyle) => (
              <ToolRender
                interpolatingStyle={interpolatingStyle}
                handleActionStart={handleActionStart}
                handleActionEnd={handleActionEnd}
                scrollPosition={scrollPosition}
                tool={tool}
                liveElementId={liveElementId}
              />
            )}
          </Spring>
        );
      })}
    </div>
  );
}

export default CustomToolsContainer;
